import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, ChevronRight, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, BtnPrimary, CtaSection } from "../components/common";
import { SOLUTIONS, SOLUTION_DETAILS, PRODUCTS } from "../data/site";

export default function SolutionDetail() {
  const { slug } = useParams();
  const base = SOLUTIONS.find((s) => s.slug === slug);
  const detail = SOLUTION_DETAILS[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!base || !detail) return <Navigate to="/solutions" replace />;

  const related = (detail.relatedProducts || []).map((s) => PRODUCTS.find((p) => p.slug === s)).filter(Boolean);
  const idx = SOLUTIONS.findIndex((s) => s.slug === slug);
  const next = SOLUTIONS[(idx + 1) % SOLUTIONS.length];

  return (
    <>
      <Seo
        title={`${base.title} | STERILTRON Solutions`}
        description={detail.tagline}
        path={`/solutions/${slug}`}
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 bg-brand-950 text-white overflow-hidden">
        <img src={base.image} alt={base.title} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-brand-950/70 to-brand-950/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-16 md:pb-24">
          <nav className="flex items-center gap-2 text-xs text-brand-300 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight size={12} />
            <Link to="/solutions" className="hover:text-white">Solutions</Link><ChevronRight size={12} />
            <span className="text-brand-100">{base.title}</span>
          </nav>
          <SectionLabel dark>Solution</SectionLabel>
          <h1 className="mt-6 font-heading text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-4xl">{base.title}</h1>
          <p className="mt-6 max-w-2xl text-brand-100 text-base md:text-lg leading-relaxed">{detail.tagline}</p>
          <div className="mt-8"><BtnPrimary to="/contact" testid="solution-cta-top" className="bg-white !text-brand-900 hover:bg-brand-100">Discuss Your Project</BtnPrimary></div>
        </div>
      </section>

      {/* OVERVIEW + FEATURES */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionLabel>Overview</SectionLabel>
            <p className="mt-6 text-slate-700 text-lg md:text-xl leading-relaxed">{detail.overview}</p>
          </div>
          <div className="lg:col-span-5">
            <SectionLabel>Key Capabilities</SectionLabel>
            <ul className="mt-6 space-y-4">
              {base.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-1 flex-shrink-0 w-5 h-5 bg-brand-800 text-white flex items-center justify-center"><Check size={13} /></span>
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-24 bg-brand-50/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel>How It Works</SectionLabel>
          <h2 className="mt-6 font-heading text-3xl md:text-4xl font-bold tracking-tighter text-brand-950">A controlled, validated process</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {detail.process.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="bg-white p-8">
                <span className="font-mono text-sm text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight text-brand-950">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <SectionLabel>Specifications</SectionLabel>
            <h2 className="mt-6 font-heading text-3xl md:text-4xl font-bold tracking-tighter text-brand-950">Typical capabilities</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Configurations are dimensioned to each facility; indicative ranges shown.</p>
          </div>
          <div className="lg:col-span-8">
            <table className="w-full border border-slate-200">
              <tbody>
                {detail.specs.map(([k, v], i) => (
                  <tr key={k} className={i % 2 ? "bg-white" : "bg-brand-50/40"}>
                    <td className="py-4 px-6 font-semibold text-brand-950 w-1/2 border-b border-slate-200">{k}</td>
                    <td className="py-4 px-6 text-slate-700 border-b border-slate-200 font-mono text-sm">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CASE EXAMPLES */}
      <section className="py-16 md:py-24 bg-brand-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel dark>Case Examples</SectionLabel>
          <h2 className="mt-6 font-heading text-3xl md:text-4xl font-bold tracking-tighter">Representative deployments</h2>
          <p className="mt-4 text-brand-200 text-sm max-w-xl">Illustrative demo references shown for context; verified project details available on request.</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {detail.caseExamples.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08} className="bg-brand-900 p-8 md:p-10">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-300">
                  <MapPin size={13} /> {c.region} <span className="text-brand-500">/</span> {c.sector}
                </div>
                <h3 className="mt-4 font-heading text-2xl font-semibold tracking-tight">{c.title}</h3>
                <p className="mt-3 text-brand-100 leading-relaxed">{c.result}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED PRODUCTS */}
      {related.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionLabel>Related Products</SectionLabel>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`solution-related-${p.slug}`} className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors">
                  <div className="overflow-hidden bg-slate-900">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-brand-600 font-semibold">{p.category}</div>
                    <h3 className="mt-2 font-heading text-base font-semibold tracking-tight text-brand-950 leading-snug">{p.name}</h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800">View Product <ArrowUpRight size={13} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEXT SOLUTION */}
      <section className="border-t border-slate-200">
        <Link to={`/solutions/${next.slug}`} data-testid="solution-next" className="group block max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="flex items-center justify-between gap-6">
            <div>
              <span className="text-xs uppercase tracking-widest text-slate-500">Next Solution</span>
              <h3 className="mt-2 font-heading text-2xl md:text-4xl font-bold tracking-tighter text-brand-950 group-hover:text-brand-700 transition-colors">{next.title}</h3>
            </div>
            <ArrowUpRight size={40} className="text-brand-800 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </Link>
      </section>

      <CtaSection />
    </>
  );
}
